import React, { useState } from 'react';
import { useUserPrefs } from '../UserPrefsProvider';
import { ACTIVE_SESSION_STORAGE_KEY, useActiveSession } from '../ActiveSessionContext';
import { useAuth } from '../AuthProvider';
import ActionConfirmModal from './ActionConfirmModal';

const options = [
  { value: 'tunes', label: 'Tunes' },
  { value: 'notes', label: 'Notes' },
  // { value: 'all', label: 'All' }, // disabled — all-mode logic preserved in Search/Sets/Sessions
];

const MODE_SWITCH_MESSAGES = {
  tunes: 'Tunes mode is for tracking your musical repertoire — add tunes, make sets, and log sessions. Switch to Tunes mode?',
  notes: 'Notes mode lets you capture ideas, links, tasks, and references with rich text. Switch to Notes mode?',
  all: 'All mode shows both tunes and notes together in one view. Switch to All mode?',
};

export default function ModeSwitcher({ onChange, inline }) {
  const { prefs, savePrefs } = useUserPrefs();
  const { activeSession, setActiveSession } = useActiveSession();
  const { user } = useAuth();
  const currentMode = prefs?.appMode || 'tunes';
  const [pendingMode, setPendingMode] = useState(null);
  const [pendingLockedMode, setPendingLockedMode] = useState(null);

  // Simple mode users are locked to the mode they set up for.
  // Full mode users can switch freely — no locks.
  const isLocked = (mode) =>
    Boolean(prefs?.onboardingComplete) &&
    prefs?.complexityLevel !== 'full' &&
    currentMode !== 'all' &&
    mode !== currentMode;

  const applyModeChange = async (newMode, endActiveSession = false) => {
    if (newMode === currentMode || !user) return;
    if (activeSession && endActiveSession) {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const sessionRef = doc(db, 'users', user.uid, 'sessions', activeSession.id);
        await updateDoc(sessionRef, { endedAt: new Date().toISOString() });
        setActiveSession(null);
        localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
      } catch (err) {
        console.error('Failed to end session:', err);
      }
    }

    await savePrefs({ ...prefs, appMode: newMode });
    document.body.setAttribute('data-app-mode', newMode);

    if (onChange) onChange(newMode);
  };

  const handleChange = async (newMode) => {
    if (newMode === currentMode || !user) return;
    if (isLocked(newMode)) {
      setPendingLockedMode(newMode);
      return;
    }
    if (activeSession) {
      setPendingMode(newMode);
      return;
    }
    await applyModeChange(newMode, false);
  };

  const pill = (
    <div className="mode-switcher-pill">
      {options.map((opt) => {
        const isActive = currentMode === opt.value;
        const locked = isLocked(opt.value);
        return (
          <button
            key={opt.value}
            onClick={() => handleChange(opt.value)}
            className={`mode-switcher-btn ${isActive ? 'mode-switcher-btn-active' : ''}`}
            style={locked ? { opacity: 0.45 } : undefined}
          >
            {opt.label}{locked ? ' 🔒' : ''}
          </button>
        );
      })}
    </div>
  );

  const lockedModeOption = options.find(o => o.value === pendingLockedMode);

  const modals = (
    <>
      <ActionConfirmModal
        open={Boolean(pendingMode)}
        title="Active Session"
        message="You have an active session. End it and switch modes?"
        confirmLabel="End Session"
        cancelLabel="Cancel"
        danger
        onConfirm={async () => {
          if (!pendingMode) return;
          const nextMode = pendingMode;
          setPendingMode(null);
          await applyModeChange(nextMode, true);
        }}
        onCancel={() => setPendingMode(null)}
      />
      <ActionConfirmModal
        open={Boolean(pendingLockedMode)}
        title={`Switch to ${lockedModeOption?.label || ''} Mode`}
        message={pendingLockedMode ? MODE_SWITCH_MESSAGES[pendingLockedMode] : ''}
        confirmLabel="Switch Mode"
        cancelLabel="Not now"
        onConfirm={async () => {
          if (!pendingLockedMode) return;
          const nextMode = pendingLockedMode;
          setPendingLockedMode(null);
          if (activeSession) {
            setPendingMode(nextMode);
          } else {
            await applyModeChange(nextMode, false);
          }
        }}
        onCancel={() => setPendingLockedMode(null)}
      />
    </>
  );

  if (inline) {
    return <>{pill}{modals}</>;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '0.25rem 0' }}>
      {pill}
      {modals}
    </div>
  );
}
